import { useState, forwardRef, useImperativeHandle } from 'react'
import styled from 'styled-components'
import {
  ChevronDown,
  ChevronUp,
  Phone,
  MapPin,
  Globe,
  Calendar,
  Clock,
} from 'lucide-react'
import { MobileColors } from 'styles/mobileTokens'
import { useForm, useWatch } from 'react-hook-form'
import { toast } from 'react-toastify'
import { z } from 'zod'
import { zodResolver } from '@hookform/resolvers/zod'
import apiService from 'services/api'
import { Dropdown, IDropdownItem } from 'components/UI/Dropdown/Dropdown'
import { MobileNoteEditorDrawer } from './MobileNoteEditorModal'
import {
  MobileTagAssignModal,
  MobileDistributorAssignModal,
  MobileGPOAssignModal,
} from './MobileAssignModal'
import { MobileDraftEmailModal } from './MobileEmail/MobileDraftEmailModal'
import { useNavigate } from 'react-router-dom'
import {
  PiCirclesThreePlus,
  PiNotepad,
  PiHandshake,
  PiIdentificationCard,
  PiShippingContainer,
  PiHandCoins,
  PiTruck,
  PiPencilSimpleLine,
  PiPaperPlaneTilt,
  PiToolbox,
} from 'react-icons/pi'
import { formatDate2 } from 'utils/formatting'

const quickNoteSchema = z.object({
  note: z.string().optional().nullable(),
  attachments: z.any().optional().nullable(),
})

type QuickNoteInput = z.infer<typeof quickNoteSchema>

export interface CustomCompanyDataDrawerProps {
  chainProxyId?: number
  doorProxyId?: number
  companyName?: string
  doorName?: string
  // Door-specific fields
  doorAddress?: string
  doorPhone?: string
  doorWebsite?: string
  doorCategory?: string
  doorType?: string
  // Company-specific fields
  companyType?: string
  companyCategory?: string
  unmatchedDoorsCount?: number
  // Timestamps
  createdDate?: string
  modifiedDate?: string
  onEditClick?: () => void
  onViewDoorsClick?: () => void
}

const DrawerContainer = styled.div<{ $isOpen: boolean }>`
  position: fixed;
  bottom: 100px; /* Position above 60px footer nav */
  left: 0;
  right: 0;
  background: ${MobileColors.backgroundWhite};
  border-top-left-radius: 16px;
  border-top-right-radius: 16px;
  transition: transform 0.3s ease-in-out;
  transform: translateY(
    ${({ $isOpen }) => ($isOpen ? '40px' : 'calc(100% - 60px)')}
  );
  max-height: calc(
    90vh - 10px
  ); /* Account for footer nav height, +50px taller */
  display: flex;
  flex-direction: column;
  z-index: 70;
`

const DrawerHeader = styled.div`
  background: #eef5f2;
  border-top-left-radius: 12px;
  border-top-right-radius: 12px;
`

const DrawerHandle = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px;
  cursor: pointer;
  user-select: none;

  &:active {
    background: rgba(0, 0, 0, 0.05);
  }
`

const DrawerTitle = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 12px;
  font-weight: 400;
  color: #000;
  letter-spacing: 0.75px;
  svg {
    font-size: 14px;
  }
`

const NoteAndActionsRow = styled.div<{ $isOpen: boolean }>`
  display: flex;
  gap: 8px;
  padding: 8px 16px;
  align-items: flex-start;
  background: #054628;
  padding-bottom: 32px;
  ${({ $isOpen }) =>
    $isOpen &&
    'width: 100%; position: fixed; bottom: -6px; padding-bottom: 14px;'}
`

const NoteInputWrapper = styled.div`
  flex: 1;
`

const ActionsButtonWrapper = styled.div`
  flex-shrink: 0;
  position: relative;
  z-index: 80;
`

const DropdownOverlay = styled.div<{ $isVisible: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(4, 30, 18, 0.5);
  z-index: 75;
  opacity: ${({ $isVisible }) => ($isVisible ? 1 : 0)};
  pointer-events: ${({ $isVisible }) => ($isVisible ? 'auto' : 'none')};
  transition: opacity 0.2s ease-in-out;
`

const ActionsButton = styled.button`
  background: #2c6b4e;
  color: white;
  border: none;
  border-radius: 6px;
  padding: 9px 12px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  white-space: nowrap;
  display: flex;
  align-items: center;
  gap: 8px;
  svg {
    font-size: 18px;
  }
  &:active {
    background: #245a42;
  }
`

const SimpleNoteInput = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  background: white;
  border: 1px solid ${MobileColors.borderDefault};
  border-radius: 6px;
  padding: 10px 12px;
  cursor: text;
  height: 40px;
  transition: all 0.2s;
  &:hover {
    border-color: #2c6b4e;
  }
`

const SimpleNoteInputText = styled.span`
  font-size: 14px;
  font-style: italic;
  color: #c4c4c4;
  flex: 1;
`

const DrawerContent = styled.div`
  flex: 1;
  overflow-y: auto;
  margin: 0 12px;
`

const CompanyHeaderRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 24px 12px;
  background: ${MobileColors.backgroundWhite};
`

const CompanyName = styled.h2`
  font-size: 16px;
  font-weight: 600;
  color: ${MobileColors.textPrimary};
  margin: 0;
  flex: 1;
`

const TabContainer = styled.div`
  display: flex;
  border-bottom: 1px solid ${MobileColors.borderDefault};
  background: ${MobileColors.backgroundWhite};
  position: sticky;
  top: 0;
  z-index: 10;
  margin: 0 12px;
`

const Tab = styled.button<{ $active: boolean }>`
  flex: 1;
  padding: 12px 16px;
  font-size: 14px;
  font-weight: 600;
  color: ${({ $active }) =>
    $active ? MobileColors.primary : MobileColors.textSecondary};
  background: transparent;
  border: none;
  border-bottom: 2px solid
    ${({ $active }) => ($active ? MobileColors.primary : 'transparent')};
  cursor: pointer;
  transition: all 0.2s;
  &:active {
    background: ${MobileColors.backgroundOverlay};
  }
`

const TabContent = styled.div`
  padding: 16px;
`

const InfoRow = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 10px 0;
  border-bottom: 1px solid ${MobileColors.borderDefault};
  &:last-child {
    border-bottom: none;
  }
`

const InfoIcon = styled.div`
  color: ${MobileColors.textSecondary};
  flex-shrink: 0;
  margin-top: 2px;
`

const InfoContent = styled.div`
  flex: 1;
`

const InfoValue = styled.div`
  font-size: 14px;
  color: ${MobileColors.textPrimary};
`

const InfoLink = styled.a`
  font-size: 14px;
  color: ${MobileColors.textLink};
  text-decoration: underline;
`

const KeyValueRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px 0;
`

const KeyLabel = styled.span`
  font-size: 14px;
  color: ${MobileColors.textSecondary};
`

const MetricCardContainer = styled.div`
  background: #f5f5f5;
  border-radius: 8px;
  padding: 12px;
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-bottom: 12px;
`

const MetricLabel = styled.div`
  font-size: 11px;
  font-weight: 400;
  color: ${MobileColors.textSecondary};
  text-transform: uppercase;
  letter-spacing: 0.5px;
`

const MetricValue = styled.div`
  font-size: 16px;
  font-weight: 600;
  color: ${MobileColors.textPrimary};
`

const ViewDoorsButton = styled.button`
  font-size: 13px;
  font-weight: 500;
  color: ${MobileColors.textLink};
  background: white;
  border: 1px solid ${MobileColors.borderDefault};
  border-radius: 6px;
  padding: 6px 12px;
  cursor: pointer;
  width: fit-content;
  &:active {
    background: ${MobileColors.backgroundOverlay};
  }
`

const FooterRow = styled.div<{ $extraSpacing?: boolean }>`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 16px;
  margin-top: ${({ $extraSpacing }) => ($extraSpacing ? '128px' : '40px')};
  border-top: 1px solid ${MobileColors.borderDefault};
  background: ${MobileColors.backgroundOverlay};
  font-size: 12px;
  color: ${MobileColors.textSecondary};
`

const FooterItem = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
`

const EditButton = styled.button`
  display: flex;
  align-items: center;
  gap: 4px;
  color: ${MobileColors.textLink};
  font-size: 14px;
  font-weight: 500;
  background: none;
  border: none;
  cursor: pointer;
  padding: 8px 0;
  &:hover {
    text-decoration: underline;
  }
`

const EditRow = styled.div`
  display: flex;
  justify-content: center;
  padding: 40px 16px 72px;
`

export interface CustomCompanyDataDrawerHandle {
  open: () => void
  close: () => void
  toggle: () => void
}

export const CustomCompanyDataDrawer = forwardRef<
  CustomCompanyDataDrawerHandle,
  CustomCompanyDataDrawerProps
>(function CustomCompanyDataDrawer(
  {
    chainProxyId,
    doorProxyId,
    companyName,
    doorName,
    doorAddress,
    doorPhone,
    doorWebsite,
    doorCategory,
    doorType,
    companyType,
    unmatchedDoorsCount,
    createdDate,
    modifiedDate,
    onEditClick,
    onViewDoorsClick,
  },
  ref
) {
  const [isOpen, setIsOpen] = useState(false)
  const [activeTab, setActiveTab] = useState<'summary' | 'location-details'>(
    'summary'
  )
  const [isNoteExpanded, setIsNoteExpanded] = useState(false)
  const [isManageTagsOpen, setIsManageTagsOpen] = useState(false)
  const [isManageDistributorsOpen, setIsManageDistributorsOpen] =
    useState(false)
  const [isManageGPOsOpen, setIsManageGPOsOpen] = useState(false)
  const [isDraftEmailOpen, setIsDraftEmailOpen] = useState(false)
  const [isActionsDropdownOpen, setIsActionsDropdownOpen] = useState(false)
  const api = apiService()
  const navigate = useNavigate()

  useImperativeHandle(ref, () => ({
    open: () => setIsOpen(true),
    close: () => setIsOpen(false),
    toggle: () => setIsOpen((prev) => !prev),
  }))

  const {
    control,
    handleSubmit,
    reset,
    formState: { isSubmitting },
  } = useForm<QuickNoteInput>({
    resolver: zodResolver(quickNoteSchema),
    defaultValues: { note: '', attachments: [] },
  })

  const noteText = useWatch({ control, name: 'note' })
  const isSavingNote = isSubmitting

  const onSubmitQuickNote = handleSubmit(async (data) => {
    if (!data.note?.trim()) {
      toast.error('Please enter a note')
      return
    }
    try {
      await api.createNote(
        undefined, // chain_id (not used for custom companies)
        chainProxyId, // contact_company_id
        data.note || '',
        data.attachments || [],
        doorProxyId
      )
      toast.success('Note saved successfully')
      reset({ note: '', attachments: [] })
      setIsNoteExpanded(false)
    } catch (error) {
      toast.error('Failed to save note')
    }
  })

  const isDoorPage = !!doorProxyId
  const displayName = isDoorPage ? doorName : companyName

  const actionItems: IDropdownItem[] = [
    {
      label: 'Add Deal',
      icon: <PiHandshake size={16} />,
      callback: () => {
        if (chainProxyId || doorProxyId) {
          navigate(`/deals?contact_company_id=${chainProxyId}`)
        } else {
          toast.info('Add Deal - Coming soon')
        }
      },
    },
    {
      label: 'Add Contact',
      icon: <PiIdentificationCard size={16} />,
      callback: () => {
        if (chainProxyId || doorProxyId) {
          navigate(`/contacts?contact_company_id=${chainProxyId}`)
        } else {
          toast.info('Add Contact - Coming soon')
        }
      },
    },
    {
      label: 'Send Email',
      icon: <PiPaperPlaneTilt size={16} />,
      callback: () => {
        if (chainProxyId || doorProxyId) {
          setIsDraftEmailOpen(true)
        } else {
          toast.info('Send Email - Coming soon')
        }
      },
    },
    {
      label: 'Manage Tags',
      icon: <PiToolbox size={16} />,
      callback: () => {
        if (chainProxyId || doorProxyId) {
          setIsManageTagsOpen(true)
        } else {
          toast.info('Manage Tags - Coming soon')
        }
      },
    },
    {
      label: 'Add Distributor',
      icon: <PiTruck size={16} />,
      callback: () => {
        if (chainProxyId || doorProxyId) {
          setIsManageDistributorsOpen(true)
        } else {
          toast.info('Add Distributor - Coming soon')
        }
      },
    },
    {
      label: 'Add GPO',
      icon: <PiShippingContainer size={16} />,
      callback: () => {
        if (chainProxyId || doorProxyId) {
          setIsManageGPOsOpen(true)
        } else {
          toast.info('Add GPO - Coming soon')
        }
      },
    },
    {
      label: 'Manage Samples',
      icon: <PiHandCoins size={16} />,
      callback: () => {
        if (chainProxyId) {
          const params = new URLSearchParams()
          params.set('contact_company_id', String(chainProxyId))
          if (companyName) {
            params.set('company_name', companyName)
          }
          navigate(`/samples?${params.toString()}`)
        } else {
          toast.info('Manage samples - Coming soon')
        }
      },
    },
  ]

  if (isManageTagsOpen) {
    return (
      <MobileTagAssignModal
        isOpen={isManageTagsOpen}
        onClose={() => setIsManageTagsOpen(false)}
        contactCompanyId={chainProxyId || 0}
        doorProxyId={doorProxyId}
        isDoorPage={isDoorPage}
      />
    )
  }

  if (isManageDistributorsOpen) {
    return (
      <MobileDistributorAssignModal
        isOpen={isManageDistributorsOpen}
        onClose={() => setIsManageDistributorsOpen(false)}
        contactCompanyId={chainProxyId || 0}
        doorProxyId={doorProxyId}
        isDoorPage={isDoorPage}
      />
    )
  }

  if (isManageGPOsOpen) {
    return (
      <MobileGPOAssignModal
        isOpen={isManageGPOsOpen}
        onClose={() => setIsManageGPOsOpen(false)}
        contactCompanyId={chainProxyId || 0}
        doorProxyId={doorProxyId}
        isDoorPage={isDoorPage}
      />
    )
  }

  if (isDraftEmailOpen) {
    return (
      <MobileDraftEmailModal
        isOpen={isDraftEmailOpen}
        onClose={() => setIsDraftEmailOpen(false)}
        contactCompanyId={chainProxyId || 0}
        companyName={companyName || ''}
      />
    )
  }

  // Build summary content for Door pages
  const doorSummaryContent = (
    <>
      {doorPhone && (
        <InfoRow>
          <InfoIcon>
            <Phone size={16} />
          </InfoIcon>
          <InfoContent>
            <InfoLink href={`tel:${doorPhone}`}>{doorPhone}</InfoLink>
          </InfoContent>
        </InfoRow>
      )}
      {doorAddress && (
        <InfoRow>
          <InfoIcon>
            <MapPin size={16} />
          </InfoIcon>
          <InfoContent>
            <InfoLink
              href={`https://maps.google.com/?q=${encodeURIComponent(doorAddress)}`}
              target="_blank"
              rel="noopener noreferrer"
            >
              {doorAddress}
            </InfoLink>
          </InfoContent>
        </InfoRow>
      )}
      {doorWebsite && (
        <InfoRow>
          <InfoIcon>
            <Globe size={16} />
          </InfoIcon>
          <InfoContent>
            <InfoLink
              href={
                doorWebsite.startsWith('http')
                  ? doorWebsite
                  : `https://${doorWebsite}`
              }
              target="_blank"
              rel="noopener noreferrer"
            >
              {doorWebsite}
            </InfoLink>
          </InfoContent>
        </InfoRow>
      )}
      {doorCategory && (
        <KeyValueRow>
          <KeyLabel>Category</KeyLabel>
          <InfoValue>{doorCategory}</InfoValue>
        </KeyValueRow>
      )}
      {doorType && (
        <KeyValueRow>
          <KeyLabel>Type</KeyLabel>
          <InfoLink href="#">{doorType}</InfoLink>
        </KeyValueRow>
      )}
    </>
  )

  // Build summary content for Company pages
  const companySummaryContent = (
    <>
      {unmatchedDoorsCount !== undefined && unmatchedDoorsCount > 0 && (
        <MetricCardContainer>
          <MetricLabel>Unmatched Doors</MetricLabel>
          <MetricValue>{unmatchedDoorsCount}</MetricValue>
          <ViewDoorsButton onClick={onViewDoorsClick}>
            View Doors
          </ViewDoorsButton>
        </MetricCardContainer>
      )}
      {companyType && (
        <KeyValueRow>
          <KeyLabel>Type</KeyLabel>
          <InfoLink href="#">{companyType}</InfoLink>
        </KeyValueRow>
      )}
    </>
  )

  return (
    <>
      <DropdownOverlay $isVisible={isActionsDropdownOpen} />

      <DrawerContainer $isOpen={isOpen}>
        <DrawerHeader>
          <DrawerHandle onClick={() => setIsOpen(!isOpen)}>
            <DrawerTitle>
              <PiCirclesThreePlus /> CUSTOM COMPANY DATA
            </DrawerTitle>
            {isOpen ? (
              <ChevronDown size={20} color="#06512F" />
            ) : (
              <ChevronUp size={20} color="#06512F" />
            )}
          </DrawerHandle>

          {/* Note input and Actions button row */}
          <NoteAndActionsRow $isOpen={isOpen}>
            <NoteInputWrapper>
              <SimpleNoteInput onClick={() => setIsNoteExpanded(true)}>
                <PiNotepad size={18} color="#06512F" />
                <SimpleNoteInputText>Write a note here...</SimpleNoteInputText>
              </SimpleNoteInput>
            </NoteInputWrapper>
            <ActionsButtonWrapper>
              <Dropdown
                items={actionItems}
                onOpenChange={setIsActionsDropdownOpen}
                buttonComponent={
                  <ActionsButton
                    onClick={(e) => {
                      e.stopPropagation()
                    }}
                  >
                    <PiToolbox />
                    Actions
                  </ActionsButton>
                }
              />
            </ActionsButtonWrapper>
          </NoteAndActionsRow>
        </DrawerHeader>

        {isOpen && (
          <>
            <CompanyHeaderRow>
              <CompanyName>{displayName}</CompanyName>
            </CompanyHeaderRow>

            <TabContainer>
              <Tab
                $active={activeTab === 'summary'}
                onClick={() => setActiveTab('summary')}
                className="text-left"
              >
                Summary
              </Tab>
            </TabContainer>

            <DrawerContent>
              <TabContent>
                <div>
                  {isDoorPage ? doorSummaryContent : companySummaryContent}
                </div>
              </TabContent>

              {(createdDate || modifiedDate) && (
                <FooterRow $extraSpacing={!isDoorPage}>
                  {createdDate && (
                    <FooterItem>
                      <Calendar size={14} />
                      Created {formatDate2(createdDate)}
                    </FooterItem>
                  )}
                  {modifiedDate && (
                    <FooterItem>
                      <Clock size={14} />
                      Edited {formatDate2(modifiedDate)}
                    </FooterItem>
                  )}
                </FooterRow>
              )}

              <EditRow>
                <EditButton onClick={onEditClick}>
                  <PiPencilSimpleLine size={16} />
                  Edit Company
                </EditButton>
              </EditRow>
            </DrawerContent>
          </>
        )}

        <MobileNoteEditorDrawer
          isOpen={isNoteExpanded}
          onClose={() => {
            setIsNoteExpanded(false)
            reset({ note: '', attachments: [] })
          }}
          onSave={onSubmitQuickNote}
          isSaving={isSavingNote}
          control={control}
          noteName="note"
          attachmentsName="attachments"
          hasContent={!!noteText?.trim()}
        />
      </DrawerContainer>
    </>
  )
})
